import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Download, Filter, Search, X } from 'lucide-react';

export default function SmsLogsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [smsType, setSmsType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    
    loadLogs();
  }, []);

  useEffect(() => {
    // Filter logs based on search query
    if (searchQuery.trim() === '') {
      setFilteredLogs(logs);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = logs.filter(log => 
        (log.recipient_name && log.recipient_name.toLowerCase().includes(query)) ||
        (log.recipient_phone && log.recipient_phone.includes(query)) ||
        (log.content && log.content.toLowerCase().includes(query))
      );
      setFilteredLogs(filtered);
    }
  }, [searchQuery, logs]);

  const loadLogs = async (filters = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      const params = new URLSearchParams();
      if (filters.startDate || startDate) params.append('start_date', filters.startDate || startDate);
      if (filters.endDate || endDate) params.append('end_date', filters.endDate || endDate);
      if (filters.smsType || smsType) params.append('sms_type', filters.smsType || smsType);

      const response = await fetch(`${API_URL}/api/sms/logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data);
        setFilteredLogs(data);
      } else {
        console.error('Failed to load SMS logs:', response.status);
        setLogs([]);
        setFilteredLogs([]);
      }
    } catch (error) {
      console.error('Failed to load SMS logs:', error);
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadLogs({
      startDate,
      endDate,
      smsType
    });
  };

  const handleClearFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setSmsType('');
    
    loadLogs({
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      smsType: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      queued: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  const totalCreditsUsed = logs.reduce((sum, log) => sum + (log.credits_used || 0), 0);
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>SMS Logs</h1>
            <p className="text-gray-600 mt-1">View and filter SMS history</p>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">
            Total Credits Used: {logs.reduce((sum, log) => sum + (log.credits_used || 0), 0)}
          </Badge>
        </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="smsType">SMS Type</Label>
              <Select value={smsType} onValueChange={setSmsType}>
                <SelectTrigger id="smsType">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2">
              <Button onClick={handleFilter} disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Filter className="mr-2 h-4 w-4" />}
                Apply
              </Button>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Search by patient name, phone number, or message content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats - Moved to top */}
      {filteredLogs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{filteredLogs.length}</div>
              <div className="text-sm text-gray-600">Total Messages</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {filteredLogs.filter(l => l.status === 'sent').length}
              </div>
              <div className="text-sm text-gray-600">Sent Successfully</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-600">
                {filteredLogs.filter(l => l.status === 'failed').length}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-600">
                {filteredLogs.reduce((sum, log) => sum + (log.credits_used || 0), 0)}
              </div>
              <div className="text-sm text-gray-600">Credits Used</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Logs Table */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>
            SMS History ({filteredLogs.length} {searchQuery ? 'of ' + logs.length : ''} records)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchQuery ? 'No SMS logs found matching your search.' : 'No SMS logs found for the selected filters.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Carrier</TableHead>
                    <TableHead>Encoding</TableHead>
                    <TableHead className="text-center">SMS Count</TableHead>
                    <TableHead className="text-center">Credits</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log, index) => (
                    <TableRow key={index}>
                      <TableCell className="text-sm">{formatDate(log.sent_at)}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="outline">{log.sms_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{log.recipient_name || '-'}</TableCell>
                      <TableCell className="text-sm font-mono">{log.recipient_phone}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="secondary">{log.carrier}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{log.encoding === 'plain' ? 'Normal' : 'Unicode'}</TableCell>
                      <TableCell className="text-sm text-center">{log.sms_count}</TableCell>
                      <TableCell className="text-sm text-center font-semibold">{log.credits_used}</TableCell>
                      <TableCell className="text-sm">{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate" title={log.content}>
                        {log.content}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
